import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@workspace/ui/components/dialog'
import { ArrowLeft, ChevronRight, Loader2, RefreshCw, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { QueryGuard } from '@/components/query-guard'
import { PrescreenDetail } from '@/features/admin/components/prescreen-detail'
import {
  useAdminDeletePrescreenMutation,
  useAdminPrescreenByIdQuery,
  useAdminRerunPrescreenMutation,
} from '@/hooks/api'

export const Route = createFileRoute('/_protected/admin/prescreens/$id')({
  component: AdminPrescreenDetailPage,
})

const statusVariant: Record<string, 'success' | 'secondary' | 'destructive' | 'warning'> = {
  COMPLETED: 'success',
  FAILED: 'destructive',
  PENDING: 'secondary',
  PROCESSING: 'warning',
}

function AdminPrescreenDetailPage() {
  const { id } = Route.useParams()
  const prescreenQuery = useAdminPrescreenByIdQuery(id)

  const navigate = useNavigate()
  const [rerunDialogOpen, setRerunDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const rerunMutation = useAdminRerunPrescreenMutation({
    onSuccess: () => {
      setRerunDialogOpen(false)
    },
  })

  const deleteMutation = useAdminDeletePrescreenMutation({
    onSuccess: () => {
      navigate({ to: '/admin/prescreens' })
    },
  })

  return (
    <QueryGuard query={prescreenQuery} errorMessage="Failed to load pre-screen">
      {(prescreen) => (
        <>
          <nav className="border-b border-border">
            <div className="flex flex-wrap items-center gap-2 py-3">
              <Link
                to="/admin/prescreens"
                className="flex items-center gap-1 text-sm font-medium text-[#f97316] hover:underline"
              >
                <ArrowLeft className="size-4" />
                Pre-screens
              </Link>
              <ChevronRight className="size-4 text-muted-foreground" />
              <h2 className="text-sm font-medium">{prescreen.startupName ?? 'Unknown Startup'}</h2>
              {prescreen.founder && (
                <>
                  <span className="text-sm text-muted-foreground">/</span>
                  <span className="text-sm text-muted-foreground">
                    {prescreen.founder.name || prescreen.founder.email}
                  </span>
                </>
              )}
              <Badge variant={statusVariant[prescreen.status] ?? 'secondary'} size="sm">
                {prescreen.status}
              </Badge>
              <div className="ml-auto">
                <Dialog open={rerunDialogOpen} onOpenChange={setRerunDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" disabled={!prescreen.pitchDeckId}>
                      <RefreshCw className="mr-1.5 size-4" />
                      Rerun Prescreen
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Rerun Prescreen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        This will rerun the prescreen using the same pitch deck document.
                      </p>
                      <Button
                        className="w-full"
                        disabled={rerunMutation.isPending}
                        onClick={() =>
                          prescreen.pitchDeckId &&
                          rerunMutation.mutate({
                            path: { id },
                            body: { documentId: prescreen.pitchDeckId },
                          })
                        }
                      >
                        {rerunMutation.isPending ? (
                          <Loader2 className="mr-1.5 size-4 animate-spin" />
                        ) : null}
                        Run Prescreen
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="text-destructive">
                      <Trash2 className="mr-1.5 size-4" />
                      Delete
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Delete Prescreen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        This will permanently delete this prescreen and all its evaluations. This
                        action cannot be undone.
                      </p>
                      <Button
                        variant="destructive"
                        className="w-full"
                        disabled={deleteMutation.isPending}
                        onClick={() => deleteMutation.mutate({ path: { id } })}
                      >
                        {deleteMutation.isPending ? (
                          <Loader2 className="mr-1.5 size-4 animate-spin" />
                        ) : null}
                        Delete Prescreen
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            <div className="flex gap-1 -mb-px">
              <span className="border-b-2 border-[#f97316] px-3 py-2 text-sm font-medium text-foreground">
                Overview
              </span>
            </div>
          </nav>
          <PrescreenDetail prescreen={prescreen} />
        </>
      )}
    </QueryGuard>
  )
}
